// Story 6.3 (ADO-879, AC4) — the TenantContext-backed read for server-owned
// session fields; extended by Story 6.32 (ADO-1315) to carry the full session
// context (`tenantId`, `role`, `depotIds`, `accountType`).
//
// Under the BWG source-of-truth model the tenant issues NO `shoplink/*` token
// claims — the API resolves tenant/role/depots from the membership registry
// per request, and this endpoint (`GET /api/v1/membership/tenant-context`,
// served by `MembershipSessionController`) is how the SPA learns them for
// display/routing. The SPA reads it over the Bearer-attaching `apiFetch`
// transport so the API remains the source of truth.
//
// DEGRADES GRACEFULLY — any non-200, network failure, or parse error resolves
// to `null` (the session context stays cleared). It NEVER throws and NEVER
// blocks bootstrap.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { resolveBrand, type WebBrandConfig } from '../theme/brand';

/** The membership session read (Story 6.32 / ADO-1315). */
export const TENANT_CONTEXT_PATH = `${API_PREFIX}/membership/tenant-context`;

/** The server-owned session fields the SPA consumes. */
export interface TenantSession {
  readonly tenantId: string | null;
  readonly role: string | null;
  readonly depotIds: readonly string[];
  readonly accountType: string | null;
  /**
   * Story 7.16 (ADO-1373) part C — the caller's STORE code
   * (`membership.users.customer_account_code`): the BWG / central-billing customer
   * code, `X`-stripped, that the store EPOS sales/deliveries feeds join on. It is
   * what the Stock Card read passes as `storeCode`.
   *
   * `null` when unprovisioned, which is the normal case today (no customer/store
   * master feed lane yet — ADO #1700). The Stock Card then renders nothing at all
   * (AC5) and issues NO request.
   */
  readonly customerAccountCode: string | null;
  /** Resolved per-tenant brand (Story 6.35 / ADO-1693); default brand when absent. */
  readonly brandConfig: WebBrandConfig;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function asStringArray(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Read the server-owned session fields. Returns `null` on any failure
 * (network, non-200, parse) — callers treat that as "not yet known".
 */
export async function fetchTenantSession(): Promise<TenantSession | null> {
  try {
    const res = await apiFetch(TENANT_CONTEXT_PATH);
    if (!res.ok) return null;
    const body = (await res.json()) as Record<string, unknown>;
    return {
      tenantId: asString(body.tenantId),
      role: asString(body.role),
      depotIds: asStringArray(body.depotIds),
      accountType: asString(body.accountType),
      customerAccountCode: asString(body.customerAccountCode),
      brandConfig: resolveBrand(body.brandConfig),
    };
  } catch {
    return null;
  }
}
